import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Check, GraduationCap, ExternalLink, Award, Play, BookOpen, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";
import { COURSE_CATEGORY_LABELS } from "./Learn";

// Per-browser enrollment is persisted in localStorage keyed by course slug so
// returning learners pick up where they left off without re-entering name/email.
const enrollmentKey = (slug) => `reddog:enroll:${slug}`;

const LearnCourse = () => {
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrollment, setEnrollment] = useState(() => {
        try { return JSON.parse(localStorage.getItem(enrollmentKey(slug))) || null; } catch { return null; }
    });
    const [selectedLessonId, setSelectedLessonId] = useState(null);
    const [enrollForm, setEnrollForm] = useState({ learner_name: "", learner_email: "", learner_phone: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get(`/courses/${slug}`)
            .then((r) => setCourse(r.data))
            .catch(() => toast.error("Course not found"))
            .finally(() => setLoading(false));
    }, [slug]);

    const enroll = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post(`/courses/${slug}/enroll`, enrollForm);
            const fresh = {
                enrollment_id: data.enrollment_id,
                course_id: data.course_id,
                completed_lessons: data.completed_lessons || [],
                learner_name: enrollForm.learner_name,
                learner_email: enrollForm.learner_email,
            };
            localStorage.setItem(enrollmentKey(slug), JSON.stringify(fresh));
            setEnrollment(fresh);
            toast.success(data.already_enrolled ? "Welcome back!" : "You're enrolled. Let's go.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not enroll");
        } finally {
            setSubmitting(false);
        }
    };

    const completeLesson = async (lessonId) => {
        if (!enrollment) return;
        try {
            const { data } = await api.post(`/courses/${slug}/enrollments/${enrollment.enrollment_id}/complete-lesson/${lessonId}`);
            const fresh = { ...enrollment, completed_lessons: data.completed_lessons, completed_at: data.completed_at };
            localStorage.setItem(enrollmentKey(slug), JSON.stringify(fresh));
            setEnrollment(fresh);
            if (data.completed_at) toast.success("🎉 Course complete!");
        } catch (err) {
            toast.error("Could not save progress");
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-[#FDFBF7] py-20 text-center text-sm text-[#4A5568]">Loading…</div>;
    }
    if (!course) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] py-20 text-center">
                <p className="font-heading text-2xl text-[#1C252E]">Course not found.</p>
                <Link to="/learn" className="text-[#E53935] text-sm mt-3 inline-block hover:underline">← Back to courses</Link>
            </div>
        );
    }

    const lessons = course.lessons || [];
    const completedIds = enrollment?.completed_lessons || [];
    const selectedLesson = lessons.find((l) => l.id === selectedLessonId);
    const progress = lessons.length ? Math.round((completedIds.length / lessons.length) * 100) : 0;
    const certificateEarned = lessons.length > 0 && completedIds.length >= lessons.length;

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="course-detail">
            <SEO title={`${course.title} — Course`} description={course.summary.slice(0, 160)} path={`/learn/${course.slug}`} />
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
                <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935] mb-6">
                    <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to courses
                </Link>

                {!selectedLesson ? (
                    <>
                        {/* Course landing */}
                        <header className="mb-8">
                            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">{COURSE_CATEGORY_LABELS[course.category] || course.category} · {course.difficulty}</p>
                            <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">{course.title}</h1>
                            {course.summary && <p className="text-base text-[#4A5568] mt-4 max-w-3xl">{course.summary}</p>}
                            <div className="flex items-center gap-4 mt-4 text-xs text-[#4A5568] flex-wrap">
                                <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} /> {course.lesson_count} lessons</span>
                                {course.estimated_minutes > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {course.estimated_minutes} min total</span>}
                                {course.instructor && <span>Instructor: <strong className="text-[#1C252E]">{course.instructor}</strong></span>}
                            </div>
                        </header>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <aside className="lg:col-span-1 space-y-4">
                                {course.cover_image && <img src={course.cover_image} alt="" className="w-full rounded-2xl object-cover max-h-64" />}
                                {enrollment ? (
                                    <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5" data-testid="course-progress-panel">
                                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-1">Your progress</p>
                                        <p className="text-2xl font-heading text-[#1C252E]">{progress}%</p>
                                        <div className="mt-2 h-2 bg-[#F4F1EA] rounded-full overflow-hidden">
                                            <div className="h-full bg-[#E53935] transition-all" style={{ width: `${progress}%` }} />
                                        </div>
                                        <p className="text-xs text-[#4A5568] mt-2">{completedIds.length} of {lessons.length} lessons complete</p>
                                        {certificateEarned && (
                                            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid="course-certificate">
                                                <Award className="inline w-4 h-4 mr-1 -mt-0.5" strokeWidth={2} />
                                                <strong>Course complete!</strong> Show this page on a phone to a foundation board member to claim your certificate.
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <form onSubmit={enroll} className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="course-enroll-form">
                                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Enroll for free</p>
                                        <input data-testid="enroll-name" required placeholder="Your name" value={enrollForm.learner_name} onChange={(e) => setEnrollForm({ ...enrollForm, learner_name: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                        <input data-testid="enroll-email" required type="email" placeholder="Your email" value={enrollForm.learner_email} onChange={(e) => setEnrollForm({ ...enrollForm, learner_email: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                        <input data-testid="enroll-phone" placeholder="Phone (optional)" value={enrollForm.learner_phone} onChange={(e) => setEnrollForm({ ...enrollForm, learner_phone: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                        <button type="submit" disabled={submitting} data-testid="enroll-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-5 py-2.5 text-sm font-medium">
                                            <Play className="w-4 h-4" strokeWidth={2} /> {submitting ? "Enrolling…" : "Start the course"}
                                        </button>
                                        <p className="text-[11px] text-[#4A5568]">Free, no payment. We use your email only to email your certificate.</p>
                                    </form>
                                )}
                            </aside>

                            <main className="lg:col-span-2">
                                <div className="bg-white border border-black/10 rounded-2xl p-6">
                                    <h2 className="font-heading text-xl text-[#1C252E] mb-4">Lessons</h2>
                                    {lessons.length === 0 ? (
                                        <p className="text-sm text-[#4A5568] italic">No lessons published yet.</p>
                                    ) : (
                                        <ol className="space-y-2" data-testid="course-lessons-list">
                                            {lessons.map((l, idx) => {
                                                const done = completedIds.includes(l.id);
                                                return (
                                                    <li key={l.id}>
                                                        <button
                                                            onClick={() => setSelectedLessonId(l.id)}
                                                            data-testid={`lesson-row-${l.id}`}
                                                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-black/5 bg-[#FDFBF7] hover:bg-[#F2D8D5]/30 hover:border-[#E53935]/30 transition text-left"
                                                        >
                                                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${done ? "bg-green-100 text-green-800" : "bg-white border border-black/10 text-[#4A5568]"}`}>
                                                                {done ? <Check className="w-4 h-4" strokeWidth={2} /> : <span className="text-xs font-bold">{idx + 1}</span>}
                                                            </span>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-[#1C252E]">{l.title}</p>
                                                                <p className="text-[11px] text-[#4A5568]"><Clock className="inline w-3 h-3 -mt-0.5" /> {l.estimated_minutes} min{l.resources?.length ? ` · ${l.resources.length} resource${l.resources.length === 1 ? "" : "s"}` : ""}</p>
                                                            </div>
                                                            <ChevronLeft className="w-4 h-4 text-[#4A5568] rotate-180" strokeWidth={1.5} />
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    )}
                                </div>
                            </main>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Lesson view */}
                        <button onClick={() => setSelectedLessonId(null)} className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935] mb-4">
                            <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Course overview
                        </button>
                        <article className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8" data-testid={`lesson-detail-${selectedLesson.id}`}>
                            <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-2">{course.title} · Lesson {(lessons.findIndex((x) => x.id === selectedLesson.id) + 1)} of {lessons.length}</p>
                            <h1 className="font-heading text-3xl sm:text-4xl text-[#1C252E] tracking-tight leading-tight">{selectedLesson.title}</h1>
                            <p className="text-xs text-[#4A5568] mt-2 flex items-center gap-1.5"><Clock className="w-3 h-3" strokeWidth={1.5} /> {selectedLesson.estimated_minutes} min</p>

                            {selectedLesson.video_url && (
                                <div className="mt-6 aspect-video rounded-2xl overflow-hidden bg-black">
                                    <iframe
                                        src={selectedLesson.video_url}
                                        title={selectedLesson.title}
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            {selectedLesson.body && (
                                <div className="mt-6 prose prose-sm max-w-none text-[#1C252E] whitespace-pre-line leading-relaxed">{selectedLesson.body}</div>
                            )}

                            {selectedLesson.resources?.length > 0 && (
                                <div className="mt-6">
                                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">Resources</p>
                                    <ul className="space-y-1.5">
                                        {selectedLesson.resources.map((r, idx) => (
                                            <li key={idx}>
                                                <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#E53935] hover:underline">
                                                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> {r.label || r.url}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-8 pt-6 border-t border-black/5 flex items-center justify-between flex-wrap gap-3">
                                {enrollment ? (
                                    completedIds.includes(selectedLesson.id) ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 bg-green-50 px-4 py-2 rounded-full">
                                            <Check className="w-4 h-4" strokeWidth={2} /> Lesson complete
                                        </span>
                                    ) : (
                                        <button onClick={() => completeLesson(selectedLesson.id)} data-testid="lesson-complete-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                                            <Check className="w-4 h-4" strokeWidth={2} /> Mark complete
                                        </button>
                                    )
                                ) : (
                                    <p className="text-xs text-[#4A5568] italic">Enroll above to track your progress.</p>
                                )}
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const idx = lessons.findIndex((x) => x.id === selectedLesson.id);
                                        return (
                                            <>
                                                {idx > 0 && <button onClick={() => setSelectedLessonId(lessons[idx - 1].id)} className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935]">← Previous</button>}
                                                {idx < lessons.length - 1 && <button onClick={() => setSelectedLessonId(lessons[idx + 1].id)} data-testid="lesson-next-btn" className="inline-flex items-center gap-1 text-sm font-medium text-[#E53935] hover:underline">Next <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></button>}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </article>
                    </>
                )}
            </div>
        </div>
    );
};

export default LearnCourse;
